/**
 * PostingDetailPage Component
 * Detail view for posting agent transfer bond
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  getTransferBondDetails,
  submitQuote,
  getDefaultQuoteValues,
  uploadDocuments,
  markReadyForVerification,
  getFileCount,
  cancelPendingRequest,
} from '../../../services/oathTransferBondService';
import { useOathAuth } from '../../../services/oathAuthContext';
import usePermissions from '../../../hooks/usePermissions';
import {
  OathPageWrapper,
  OathSectionCard,
  OathDetailField,
  OathModal,
  OathButton,
  OathInput,
  OathAlert,
} from '../../shared/OathUI';
import BondInfoSection from './posting/BondInfoSection';
import ExtendedSummaryFields from './requesting/ExtendedSummaryFields';
import RequestPowersSection from './requesting/RequestPowersSection';
import RequestDocumentsSection from './requesting/RequestDocumentsSection';
import { formatCurrency, formatDate, formatDateTime, formatPhoneNumber, formatSSN } from './shared/formatUtils';

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i}>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Status badge component
const StatusBadge = ({ bondDetails, userQuote }) => {
  let label = 'Open';
  let colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';

  if (bondDetails?.isDeferred === 'Y') {
    label = 'Cancelled';
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  } else if (bondDetails?.statusCd === 'C') {
    label = 'Closed';
    colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  } else if (bondDetails?.hasBeenPosted) {
    label = 'Verification';
    colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
  } else if (bondDetails?.executingAgentID) {
    label = 'Accepted';
    colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  } else if (userQuote) {
    label = 'Quoted';
    colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

// Use centralized OathDetailField and OathSectionCard from OathUI

// Submit Quote Modal
const SubmitQuoteModal = ({ isOpen, onClose, onSubmit, bondDetails, defaultValues, isSubmitting }) => {
  const [executionFee, setExecutionFee] = useState('0');
  const [travelFee, setTravelFee] = useState('0');
  const [miscFee, setMiscFee] = useState('0');
  const [feeDescription, setFeeDescription] = useState('');
  const [postingTime, setPostingTime] = useState('');
  const [validDuration, setValidDuration] = useState('');
  const [stipulations, setStipulations] = useState('');
  const [paymentUpfront, setPaymentUpfront] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Load default values only when modal opens (not on every re-render from polling)
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (isOpen && defaultValues && !initialized) {
      setExecutionFee(defaultValues.tbondExecutionFeeQuote?.toString() || '0');
      setTravelFee(defaultValues.tbondTravelQuote?.toString() || '0');
      setMiscFee(defaultValues.tbondMisQuote?.toString() || '0');
      setPostingTime(defaultValues.timeToPost ? (defaultValues.timeToPost / 60).toString() : '');
      setValidDuration(defaultValues.quoteDuration ? (defaultValues.quoteDuration / 60).toString() : '');
      setInitialized(true);
    }
    if (!isOpen) {
      setInitialized(false);
    }
  }, [isOpen, defaultValues, initialized]);

  const handleSubmit = () => {
    if (!travelFee || !postingTime || !validDuration) {
      setValidationError('Please fill out all required fields.');
      return;
    }
    if (parseInt(postingTime) < 1 || parseInt(validDuration) < 1) {
      setValidationError('Posting time and quote duration must be at least 1 hour.');
      return;
    }
    setValidationError('');

    onSubmit({
      executionFee: parseFloat(executionFee) || 0,
      travelFee: parseFloat(travelFee) || 0,
      otherFee: parseFloat(miscFee) || 0,
      feeDescription: paymentUpfront
        ? feeDescription ? `${feeDescription}; State taxes` : 'State taxes'
        : feeDescription,
      promisedPostingTime: parseInt(postingTime) * 60,
      quotesValidDuration: parseInt(validDuration) * 60,
      postingStipulations: stipulations,
      paymentRequiredUpFront: paymentUpfront,
    });
  };

  const totalFees = (parseFloat(executionFee) || 0) + (parseFloat(travelFee) || 0) + (parseFloat(miscFee) || 0);

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Submit Quote"
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            disabled={isSubmitting || !travelFee || !postingTime || !validDuration}
            loading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Quote'}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Ref # */}
        <p className="text-sm text-gray-500 dark:text-gray-400">Ref #{bondDetails?.transferBondTransactionID}</p>

        {/* Validation error */}
        {validationError && (
          <OathAlert variant="error" message={validationError} />
        )}

        {/* Request Info */}
        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Jail</p>
            <p className="font-medium text-gray-900 dark:text-white">{bondDetails?.jailLocation || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">State</p>
            <p className="font-medium text-gray-900 dark:text-white">{bondDetails?.state || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Bonds</p>
            <p className="font-medium text-gray-900 dark:text-white">{bondDetails?.numberOfBonds || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Courthouse</p>
            <p className="font-medium text-gray-900 dark:text-white">{bondDetails?.courtPlace || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">County</p>
            <p className="font-medium text-gray-900 dark:text-white">{bondDetails?.county || '-'}</p>
          </div>
        </div>

        {/* Fees Section */}
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">Fees</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Execution fee is standard and doesn&apos;t change, travel is reasonable travel expenses, misc is jail fees and/or any additional expense.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <OathInput
              label="Execution Fee"
              type="number"
              value={executionFee}
              onChange={setExecutionFee}
              prefix="$"
              disabled
            />
            <OathInput
              label="Travel Fee"
              type="number"
              value={travelFee}
              onChange={setTravelFee}
              prefix="$"
              required
            />
            <OathInput
              label="Misc Fee"
              type="number"
              value={miscFee}
              onChange={setMiscFee}
              prefix="$"
            />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Total: <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(totalFees)}</span>
          </p>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fee Description</label>
            <textarea
              value={feeDescription}
              onChange={(e) => setFeeDescription(e.target.value)}
              placeholder="Describe any miscellaneous fees..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              rows={2}
            />
          </div>
        </div>

        {/* Posting Details */}
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">Posting Details</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            The posting details represent how long it takes you to post this bond and how long the quote is valid for.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <OathInput
              label="Posting Time (hours)"
              type="number"
              value={postingTime}
              onChange={setPostingTime}
              placeholder="e.g., 4"
              required
              helperText="How long it will take you to post this bond"
            />
            <OathInput
              label="Quote Valid (hours)"
              type="number"
              value={validDuration}
              onChange={setValidDuration}
              placeholder="e.g., 3"
              required
              helperText="How long is this quote good for?"
            />
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Posting Stipulations</label>
            <textarea
              value={stipulations}
              onChange={(e) => setStipulations(e.target.value)}
              placeholder="Any conditions or notes for the requesting agent..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              rows={2}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Posting stipulations can be considered as a message to the requesting agent.
            </p>
          </div>

          {bondDetails?.state === 'LA' && (
            <div className="mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentUpfront}
                  onChange={(e) => setPaymentUpfront(e.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Payment Required Up Front</span>
              </label>
            </div>
          )}
        </div>
      </div>
    </OathModal>
  );
};

// File Upload Section
const FileUploadSection = ({ bondDetails, onRefresh, onFileCountChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [powerCount, setPowerCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    if (!bondDetails?.transferBondTransactionID) return;
    const response = await getFileCount(bondDetails.transferBondTransactionID);
    if (response.success) {
      setFileCount(response.count || 0);
      setPowerCount(response.powerCount || 0);
    }
  }, [bondDetails?.transferBondTransactionID]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Report fileCount to parent for Ready for Verification guard
  useEffect(() => {
    onFileCountChange?.(fileCount);
  }, [fileCount, onFileCountChange]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      const response = await uploadDocuments(bondDetails.transferBondTransactionID, selectedFiles);
      if (response.success) {
        toast.success('Files uploaded successfully.');
        setSelectedFiles([]);
        fetchCounts();
        onRefresh();
      } else {
        toast.error('Failed to upload files.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <InformationCircleIcon className="h-4 w-4 text-primary" />
          <span>Files: <strong>{fileCount}</strong></span>
        </div>
        <div className="flex items-center gap-1">
          <InformationCircleIcon className="h-4 w-4 text-primary" />
          <span>Powers: <strong>{powerCount}</strong></span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors cursor-pointer">
          <ArrowUpTrayIcon className="h-4 w-4" />
          Choose Files
          <input
            type="file"
            multiple
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>

        {selectedFiles.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-white font-medium rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
          </button>
        )}
      </div>

      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Selected Files:</p>
          {selectedFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
              >
                <XMarkIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!bondDetails?.hasBeenPosted && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              No changes can be made after submitting for verification. Contact Bankers at 877-778-5245 if changes are needed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const PostingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useOathAuth();
  const { can } = usePermissions();
  const canViewRequestingAgency = can('READ:TRANSFER-BOND-REQUESTING-AGENCY');
  const canViewPostingAgency = can('READ:TRANSFER-BOND-POSTING-AGENCY');

  const [bondDetails, setBondDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [defaultQuoteValues, setDefaultQuoteValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);

  // Get user's agency ID from auth context
  const agencyId = user?.agencies?.[0]?.agencyId;

  // Fetch bond details
  const fetchDetails = useCallback(async () => {
    if (!id) return;

    try {
      const response = await getTransferBondDetails(id);
      if (response.success) {
        setBondDetails(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to load details');
      }
    } catch (err) {
      setError('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Initial fetch and polling
  useEffect(() => {
    fetchDetails();
    const interval = setInterval(fetchDetails, 10000);
    return () => clearInterval(interval);
  }, [fetchDetails]);

  // Check URL action params (clear param after handling so polling doesn't re-trigger)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'quote' && bondDetails && !bondDetails.executingAgentID) {
      openQuoteModal();
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, bondDetails]);

  // Open quote modal and fetch defaults
  const openQuoteModal = async () => {
    if (agencyId && bondDetails?.transferBondTransactionID) {
      const response = await getDefaultQuoteValues(agencyId, bondDetails.transferBondTransactionID);
      if (response.success) {
        setDefaultQuoteValues(response.data);
      }
    }
    setQuoteModalOpen(true);
  };

  // Submit quote
  const handleSubmitQuote = async (quoteData) => {
    setIsSubmitting(true);
    try {
      const response = await submitQuote(id, {
        ...quoteData,
        agentID: agencyId,
        transferBondTransactionID: parseInt(id),
      });
      if (response.success) {
        toast.success('Quote submitted successfully.');
        setQuoteModalOpen(false);
        fetchDetails();
      } else {
        toast.error(response.error || 'Failed to submit quote.');
        setError(response.error || 'Failed to submit quote');
      }
    } catch (err) {
      toast.error('Failed to submit quote.');
      setError('Failed to submit quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mark ready for verification (called from confirmation modal)
  const handleReadyForVerification = async () => {
    setVerifyConfirmOpen(false);
    setIsSubmitting(true);
    try {
      const response = await markReadyForVerification(id);
      if (response.success) {
        toast.success('Transaction marked as ready for verification.');
        fetchDetails();
      } else {
        toast.error(response.error || 'Failed to mark ready for verification.');
        setError(response.error || 'Failed to mark ready for verification');
      }
    } catch (err) {
      toast.error('Failed to mark ready for verification.');
      setError('Failed to mark ready for verification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel quote
  const handleCancelQuote = async () => {
    if (!window.confirm('Are you sure you want to cancel your quote?')) return;

    setIsSubmitting(true);
    try {
      const response = await cancelPendingRequest(id, agencyId);
      if (response.success) {
        toast.success('Quote cancelled successfully.');
        navigate('/app/transfer-bonds?tab=posting');
      } else {
        toast.error(response.error || 'Failed to cancel quote.');
        setError(response.error || 'Failed to cancel quote');
      }
    } catch (err) {
      toast.error('Failed to cancel quote.');
      setError('Failed to cancel quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Find user's quote
  const userQuote = bondDetails?.quotes?.find((q) => q.agentID === agencyId);
  const isAccepted = bondDetails?.executingAgentID === agencyId;
  const canQuote = !bondDetails?.executingAgentID && bondDetails?.isDeferred !== 'Y' && !userQuote;

  if (loading) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <LoadingSkeleton />
      </OathPageWrapper>
    );
  }

  if (error && !bondDetails) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate('/app/transfer-bonds?tab=posting')}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Go back to list
          </button>
        </div>
      </OathPageWrapper>
    );
  }

  const defendantName = `${bondDetails?.defendantFirstName || ''} ${bondDetails?.defendantLastName || ''}`.trim() || 'Unknown';

  // Calculate deadline for accepted quote
  let deadlineText = null;
  if (isAccepted && bondDetails?.acceptedQuoteID) {
    const acceptedQuote = bondDetails.quotes?.find((q) => q.transferBondQuoteID === bondDetails.acceptedQuoteID);
    if (acceptedQuote?.acceptedDate && acceptedQuote?.promisedPostingTimeInMinutes) {
      const deadline = new Date(new Date(acceptedQuote.acceptedDate).getTime() + acceptedQuote.promisedPostingTimeInMinutes * 60 * 1000);
      deadlineText = formatDateTime(deadline);
    }
  }

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app/transfer-bonds?tab=posting')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{defendantName}</h1>
            <StatusBadge bondDetails={bondDetails} userQuote={userQuote} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Reference #{bondDetails?.transferBondTransactionID}</p>
        </div>
        <button
          onClick={fetchDetails}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Error alert */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Status Messages */}
      {bondDetails?.isDeferred === 'Y' && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <h3 className="font-semibold text-red-900 dark:text-red-300 mb-1">Request Cancelled</h3>
          <p className="text-sm text-red-700 dark:text-red-400">This request has been cancelled by the requesting agent.</p>
        </div>
      )}

      {bondDetails?.statusCd === 'C' && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Transaction Complete</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">This transaction has been closed.</p>
        </div>
      )}

      {isAccepted && !bondDetails?.hasBeenPosted && bondDetails?.statusCd !== 'C' && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h3 className="font-semibold text-green-900 dark:text-green-300">Your Quote Was Accepted!</h3>
          </div>
          {deadlineText && (
            <p className="text-sm text-green-700 dark:text-green-400">
              Post by: <span className="font-medium">{deadlineText}</span>
            </p>
          )}
          <p className="text-sm text-green-700 dark:text-green-400 mt-1">
            Upload powers and documents below, then submit for verification.
          </p>
        </div>
      )}

      {bondDetails?.hasBeenPosted && bondDetails?.statusCd !== 'C' && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <h3 className="font-semibold text-purple-900 dark:text-purple-300 mb-1">Awaiting Verification</h3>
          <p className="text-sm text-purple-700 dark:text-purple-400">
            You have submitted the bond information for verification. Bankers will review and close the transaction.
          </p>
        </div>
      )}

      {userQuote && !isAccepted && !bondDetails?.executingAgentID && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-1">Quote Pending</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">Your quote of {formatCurrency(
                (userQuote.executionFee || 0) + (userQuote.travelFee || 0) + (userQuote.otherFee || 0)
              )} is waiting for a response.</p>
            </div>
            <button
              onClick={handleCancelQuote}
              disabled={isSubmitting}
              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
            >
              Cancel Quote
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {canQuote && bondDetails?.statusCd !== 'C' && (
        <div className="mb-6">
          <button
            onClick={openQuoteModal}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4" />
            Submit Quote
          </button>
        </div>
      )}

      {/* Upload Section - Only for accepted quotes */}
      {isAccepted && bondDetails?.statusCd !== 'C' && (
        <OathSectionCard
          title={bondDetails?.hasBeenPosted ? 'Awaiting Verification' : 'Upload Powers & Documents'}
          icon={ArrowUpTrayIcon}
          className="mb-6"
          actions={
            !bondDetails?.hasBeenPosted && (
              <button
                onClick={() => setVerifyConfirmOpen(true)}
                disabled={isSubmitting || uploadedFileCount === 0}
                className="px-3 py-1.5 text-sm bg-secondary text-white font-medium rounded hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={uploadedFileCount === 0 ? 'Upload at least one document first' : ''}
              >
                Ready for Verification
              </button>
            )
          }
        >
          <FileUploadSection bondDetails={bondDetails} onRefresh={fetchDetails} onFileCountChange={setUploadedFileCount} />
        </OathSectionCard>
      )}

      {/* Bond Info / Powers Management - For accepted quotes, manage individual power entries */}
      {isAccepted && bondDetails?.statusCd !== 'C' && (
        <OathSectionCard title="Bond Powers" icon={DocumentTextIcon} className="mb-6">
          <BondInfoSection bondDetails={bondDetails} onRefresh={fetchDetails} />
        </OathSectionCard>
      )}

      {/* Bond Information */}
      <OathSectionCard title="Bond Details" icon={InformationCircleIcon} className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="Reference #" value={bondDetails?.transferBondTransactionID} />
          <OathDetailField label="Total Bond Amount" value={formatCurrency(bondDetails?.totalBondAmount)} highlight />
          <OathDetailField label="Number of Bonds" value={bondDetails?.numberOfBonds} />
          <OathDetailField label="Created" value={formatDate(bondDetails?.createdAt)} />
          {canViewRequestingAgency && (
            <OathDetailField label="Requesting Agency" value={bondDetails?.requestingAgencyName} />
          )}
        </div>
      </OathSectionCard>

      {/* Defendant Information */}
      <OathSectionCard title="Defendant" className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="Name" value={defendantName} />
          <OathDetailField label="Date of Birth" value={formatDate(bondDetails?.defendantDateOfBirth)} />
          <OathDetailField label="SSN" value={formatSSN(bondDetails?.defendantSsn)} />
          <OathDetailField label="Phone" value={formatPhoneNumber(bondDetails?.defendantPhone)} />
          <OathDetailField label="Driver's License" value={bondDetails?.defendantDriversLicenseNumber} />
          <OathDetailField label="DL State" value={bondDetails?.driversLicenseState} />
          <OathDetailField label="Gender" value={bondDetails?.defendantGender} />
          <OathDetailField label="Race" value={bondDetails?.defendantRace} />
        </div>
      </OathSectionCard>

      {/* Location Information */}
      <OathSectionCard title="Location & Court" className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4">
          <OathDetailField label="County" value={bondDetails?.county} />
          <OathDetailField label="State" value={bondDetails?.state} />
          <OathDetailField label="Jail" value={bondDetails?.jailLocation} />
          <OathDetailField label="Jail Phone" value={formatPhoneNumber(bondDetails?.jailPhone)} />
          <OathDetailField label="Court" value={bondDetails?.courtPlace} />
          <OathDetailField label="Court Code" value={bondDetails?.courtCode} />
          <OathDetailField label="Booking Number" value={bondDetails?.bookingNumber} />
          <OathDetailField label="Arrest Date" value={formatDate(bondDetails?.arrestDate)} />
        </div>
      </OathSectionCard>

      {/* Extended Summary Fields - Physical description, identifiers, next of kin, etc. */}
      <ExtendedSummaryFields
        bondDetails={bondDetails}
        canViewPostingAgency={canViewPostingAgency}
        canViewRequestingAgency={canViewRequestingAgency}
      />

      {/* Fee Information */}
      {!!(bondDetails?.executionFee || bondDetails?.travelFee || bondDetails?.otherFees) && (
        <OathSectionCard title="Fee Information" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4">
            <OathDetailField label="Execution Fee" value={formatCurrency(bondDetails?.executionFee)} />
            <OathDetailField label="Travel Fee" value={formatCurrency(bondDetails?.travelFee)} />
            <OathDetailField label="Other Fees" value={formatCurrency(bondDetails?.otherFees)} />
          </div>
        </OathSectionCard>
      )}

      {/* Read-only powers display (for non-accepted or closed states) */}
      {(!isAccepted || bondDetails?.statusCd === 'C') && (
        <RequestPowersSection bondDetails={bondDetails} />
      )}

      {/* Uploaded Documents */}
      <RequestDocumentsSection bondDetails={bondDetails} />

      {/* Ready for Verification Confirmation Modal */}
      <OathModal
        isOpen={verifyConfirmOpen}
        onClose={() => setVerifyConfirmOpen(false)}
        title="Submit for Verification"
        size="sm"
        footer={
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
            <OathButton
              variant="outline"
              onClick={() => setVerifyConfirmOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </OathButton>
            <OathButton
              onClick={handleReadyForVerification}
              loading={isSubmitting}
              className="w-full sm:w-auto"
            >
              Confirm
            </OathButton>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium text-gray-900 dark:text-white mb-2">Are you sure you want to submit for verification?</p>
            <p>No changes can be made after this. If changes are needed, contact Bankers at <span className="font-medium text-gray-900 dark:text-white">877-778-5245</span>.</p>
          </div>
        </div>
      </OathModal>

      {/* Submit Quote Modal */}
      <SubmitQuoteModal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        onSubmit={handleSubmitQuote}
        bondDetails={bondDetails}
        defaultValues={defaultQuoteValues}
        isSubmitting={isSubmitting}
      />
    </OathPageWrapper>
  );
};

export default PostingDetailPage;
